import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Stack,
  Button,
  Chip,
  Snackbar,
  Alert,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import SearchIcon from '@mui/icons-material/Search';
import ApartmentIcon from '@mui/icons-material/Apartment';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';
import AdsClickIcon from '@mui/icons-material/AdsClick';
import HistoryIcon from '@mui/icons-material/History';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';
import RestoreIcon from '@mui/icons-material/Restore';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined';
import LinearProgress from '@mui/material/LinearProgress';

import {
  KeywordItem,
  NegativeKeyword,
  PropertySeoItem,
  LocalityHubSeoItem,
  PortalHomepageSeoItem,
  SeoSnapshotBackup,
  ScheduledReleaseItem,
  GoogleAdGroup,
  SeoEngineConfig,
  SeoPendingApprovalItem,
  SeoEngineMode,
} from '../types/seo';

import {
  INITIAL_NEGATIVES,
  INITIAL_PROPERTY_LISTINGS,
  INITIAL_HOMEPAGE_SEO,
  INITIAL_GOOGLE_AD_GROUPS,
} from '../utils/seoAIEngine';
import { stripNumericPrices } from '../utils/priceSanitizer';

import KeywordIntelligenceTab from '../components/seo/KeywordIntelligenceTab';
import PropertyListingsSeoTab from '../components/seo/PropertyListingsSeoTab';
import LocalityHubsSeoTab from '../components/seo/LocalityHubsSeoTab';
import PortalHomepageSeoTab from '../components/seo/PortalHomepageSeoTab';
import GoogleAdsBridgeTab from '../components/seo/GoogleAdsBridgeTab';
import { WebsiteCmsService } from '../services/websiteCmsService';

const STORAGE_KEYS = {
  KEYWORDS: 'swarna_seo_measured_keywords_v1',
  NEGATIVES: 'swarna_seo_negatives_v4',
  PROPERTIES: 'swarna_seo_properties_v4',
  HUBS: 'swarna_seo_hubs_v5',
  HOMEPAGE: 'swarna_seo_homepage_v4',
  BACKUPS: 'swarna_seo_backups_v4',
  QUEUE: 'swarna_seo_queue_v4',
  AD_GROUPS: 'swarna_seo_ad_groups_v4',
};

export default function SeoManagementPage() {
  const [activeTab, setActiveTab] = useState<number>(0);

  // State Management with Local Persistence
  const [keywords, setKeywords] = useState<KeywordItem[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.KEYWORDS);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [negatives, setNegatives] = useState<NegativeKeyword[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.NEGATIVES);
      return saved ? JSON.parse(saved) : INITIAL_NEGATIVES;
    } catch {
      return INITIAL_NEGATIVES;
    }
  });

  const [properties, setProperties] = useState<PropertySeoItem[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.PROPERTIES);
      const list: PropertySeoItem[] = saved ? JSON.parse(saved) : INITIAL_PROPERTY_LISTINGS;
      return list.map((p) => ({
        ...p,
        metaDescription: stripNumericPrices(p.metaDescription),
        seoTitle: stripNumericPrices(p.seoTitle),
      }));
    } catch {
      return INITIAL_PROPERTY_LISTINGS;
    }
  });

  const [localityHubs, setLocalityHubs] = useState<LocalityHubSeoItem[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.HUBS);
      const list: LocalityHubSeoItem[] = saved ? JSON.parse(saved) : [];
      return list.map((h) => ({
        ...h,
        metaDescription: stripNumericPrices(h.metaDescription),
        richGuideHtml: stripNumericPrices(h.richGuideHtml),
        seoTitle: stripNumericPrices(h.seoTitle),
        title: stripNumericPrices(h.title),
      }));
    } catch {
      return [];
    }
  });

  const [homepageSeo, setHomepageSeo] = useState<PortalHomepageSeoItem>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.HOMEPAGE);
      const item: PortalHomepageSeoItem = saved ? JSON.parse(saved) : INITIAL_HOMEPAGE_SEO;
      return {
        ...item,
        metaDescription: stripNumericPrices(item.metaDescription),
        seoTitle: stripNumericPrices(item.seoTitle),
      };
    } catch {
      return INITIAL_HOMEPAGE_SEO;
    }
  });

  const [backups, setBackups] = useState<SeoSnapshotBackup[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.BACKUPS);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [releaseQueue, setReleaseQueue] = useState<ScheduledReleaseItem[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.QUEUE);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [adGroups, setAdGroups] = useState<GoogleAdGroup[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.AD_GROUPS);
      return saved ? JSON.parse(saved) : INITIAL_GOOGLE_AD_GROUPS;
    } catch {
      return INITIAL_GOOGLE_AD_GROUPS;
    }
  });

  // Autonomous Engine Configuration & Month 1 Review Queue
  const [engineConfig, setEngineConfig] = useState<SeoEngineConfig>({
    mode: 'supervised_review', // Month 1 default: Supervised Review
    autoSyncEnabled: false,
    minAuditScoreThreshold: 85,
    notifyOnPendingReview: true,
    totalAutoOptimizations: 0,
  });
  const [pendingReviews, setPendingReviews] = useState<SeoPendingApprovalItem[]>([]);
  const [loadingInitial, setLoadingInitial] = useState<boolean>(true);

  // Notifications
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);

  // Load live Firestore SEO state on mount
  useEffect(() => {
    // Retire the previous browser-stored service account key workflow.
    localStorage.removeItem('swarna_gsc_auth_v1');
    const loadLiveSeoState = async () => {
      setLoadingInitial(true);
      try {
        const [config, pendingList, liveRecords, savedHubs] = await Promise.all([
          WebsiteCmsService.getSeoEngineConfig(),
          WebsiteCmsService.getPendingSeoReviews(),
          WebsiteCmsService.getAllLiveSeoRecords(),
          WebsiteCmsService.getAllFirestoreLocalityHubs(),
        ]);

        setEngineConfig(config);
        setPendingReviews(pendingList);
        if (savedHubs.length) {
          setLocalityHubs((previous) => {
            const bySlug = new Map(previous.map((hub) => [hub.slug, hub]));
            savedHubs.forEach((hub) => bySlug.set(hub.slug, hub));
            return Array.from(bySlug.values());
          });
        }

        // Update properties if live records exist
        if (liveRecords && Object.keys(liveRecords).length > 0) {
          setProperties((prev) =>
            prev.map((p) => {
              const live = liveRecords[p.projectKey];
              if (live) {
                return {
                  ...p,
                  seoTitle: live.metaTitle || p.seoTitle,
                  metaDescription: live.metaDescription || p.metaDescription,
                  faqs: live.faqs || p.faqs,
                  schemaJson: live.schemaJson || p.schemaJson,
                  syncStatus: 'synced',
                  lastSyncedAt: live.updatedAt ? new Date(live.updatedAt).toLocaleString('en-IN') : p.lastSyncedAt,
                };
              }
              return p;
            })
          );

          // Update homepage if live record exists
          if (liveRecords['homepage']) {
            const liveH = liveRecords['homepage'];
            setHomepageSeo((prev) => ({
              ...prev,
              seoTitle: liveH.metaTitle || prev.seoTitle,
              metaDescription: liveH.metaDescription || prev.metaDescription,
              faqs: liveH.faqs || prev.faqs,
              schemaJson: liveH.schemaJson || prev.schemaJson,
              syncStatus: 'synced',
              lastSyncedAt: liveH.updatedAt ? new Date(liveH.updatedAt).toLocaleString('en-IN') : prev.lastSyncedAt,
            }));
          }

          // Update locality hubs if live records exist
          setLocalityHubs((prev) =>
            prev.map((hub) => {
              const liveHub = liveRecords[hub.slug];
              if (liveHub) {
                return {
                  ...hub,
                  seoTitle: liveHub.metaTitle || hub.seoTitle,
                  metaDescription: liveHub.metaDescription || hub.metaDescription,
                  faqs: liveHub.faqs || hub.faqs,
                  schemaJson: liveHub.schemaJson || hub.schemaJson,
                  syncStatus: 'synced',
                  lastSyncedAt: liveHub.updatedAt ? new Date(liveHub.updatedAt).toLocaleString('en-IN') : hub.lastSyncedAt,
                };
              }
              return hub;
            })
          );
        }
      } catch (err) {
        console.warn('Initial live SEO fetch failed:', err);
      } finally {
        setLoadingInitial(false);
      }
    };

    loadLiveSeoState();
  }, []);

  // Autonomous Engine Mode Toggle
  const handleToggleEngineMode = async (newMode: SeoEngineMode) => {
    const updatedConfig = { ...engineConfig, mode: newMode };
    setEngineConfig(updatedConfig);
    await WebsiteCmsService.saveSeoEngineConfig(updatedConfig);
    setSnackbarMessage(
      newMode === 'supervised_review'
        ? '🛡️ Mode Set: Month 1 Supervised Review Active. AI drafts changes for your 1-click verification.'
        : '⚡ Mode Set: Full Autonomous Autopilot Active. Changes push straight to live website.'
    );
  };

  // 1-Click Approve Pending Review Item
  const handleApproveReview = async (item: SeoPendingApprovalItem) => {
    try {
      await WebsiteCmsService.approveSeoReview(item);
      setPendingReviews((prev) => prev.filter((r) => r.id !== item.id));

      // Update local state
      if (item.entityType === 'PROPERTY') {
        setProperties((prev) =>
          prev.map((p) => (p.projectKey === item.targetSlug ? { ...p, seoTitle: item.proposedTitle, metaDescription: item.proposedDescription, faqs: item.proposedFaqs, schemaJson: item.proposedSchemaJson, syncStatus: 'synced' } : p))
        );
      } else if (item.entityType === 'LOCALITY') {
        setLocalityHubs((prev) =>
          prev.map((h) => (h.slug === item.targetSlug ? { ...h, seoTitle: item.proposedTitle, metaDescription: item.proposedDescription, faqs: item.proposedFaqs, schemaJson: item.proposedSchemaJson, syncStatus: 'synced' } : h))
        );
      } else if (item.entityType === 'HOMEPAGE') {
        setHomepageSeo((prev) => ({ ...prev, seoTitle: item.proposedTitle, metaDescription: item.proposedDescription, faqs: item.proposedFaqs, schemaJson: item.proposedSchemaJson, syncStatus: 'synced' }));
      }

      setSnackbarMessage(`✅ Approved and pushed live for ${item.entityName}!`);
    } catch (err) {
      setSnackbarMessage('❌ Approval sync failed. Check internet connection.');
    }
  };

  // 1-Click Approve All Pending Reviews
  const handleApproveAllPending = async () => {
    try {
      await WebsiteCmsService.approveAllSeoReviews(pendingReviews);
      setPendingReviews([]);
      setSnackbarMessage(`🎉 Approved all ${pendingReviews.length} pending recommendations to live website!`);
    } catch (err) {
      setSnackbarMessage('❌ Batch approval failed.');
    }
  };

  // Dismiss Pending Review Item
  const handleDismissReview = async (reviewId: string) => {
    await WebsiteCmsService.rejectSeoReview(reviewId);
    setPendingReviews((prev) => prev.filter((r) => r.id !== reviewId));
    setSnackbarMessage('Dismissed recommendation.');
  };

  // Sync to local storage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.KEYWORDS, JSON.stringify(keywords));
      localStorage.setItem(STORAGE_KEYS.NEGATIVES, JSON.stringify(negatives));
      localStorage.setItem(STORAGE_KEYS.PROPERTIES, JSON.stringify(properties));
      localStorage.setItem(STORAGE_KEYS.HUBS, JSON.stringify(localityHubs));
      localStorage.setItem(STORAGE_KEYS.HOMEPAGE, JSON.stringify(homepageSeo));
      localStorage.setItem(STORAGE_KEYS.BACKUPS, JSON.stringify(backups));
      localStorage.setItem(STORAGE_KEYS.QUEUE, JSON.stringify(releaseQueue));
      localStorage.setItem(STORAGE_KEYS.AD_GROUPS, JSON.stringify(adGroups));
    } catch (e) {
      console.error('Storage sync failed', e);
    }
  }, [keywords, negatives, properties, localityHubs, homepageSeo, backups, releaseQueue, adGroups]);

  // Handlers for Module 1
  const handleAddKeyword = (kw: KeywordItem) => {
    setKeywords((prev) => [kw, ...prev]);
    setSnackbarMessage(`Ingested query: "${kw.query}" to intelligence pool.`);
  };

  const handleExcludeKeyword = (keywordId: string, reason: string, stem: string, isPermanent: boolean) => {
    setKeywords((prev) =>
      prev.map((k) => (k.id === keywordId ? { ...k, isExcluded: true, exclusionReason: reason } : k))
    );
    if (stem.trim()) {
      const newNeg: NegativeKeyword = {
        id: `neg-${Date.now()}`,
        term: stem.trim().toLowerCase(),
        category: 'Rental / PG',
        stem: stem.trim().toLowerCase(),
        matchType: 'phrase',
        isPermanent,
        addedAt: new Date().toISOString().split('T')[0],
      };
      setNegatives((prev) => [newNeg, ...prev]);
    }
    setSnackbarMessage(`Excluded query & added "${stem}" to negative filter.`);
  };

  const handleRemoveNegative = (id: string) => {
    setNegatives((prev) => prev.filter((n) => n.id !== id));
    setSnackbarMessage('Removed negative keyword.');
  };

  const handleMergeKeywords = (newKws: KeywordItem[]) => {
    setKeywords((prev) => {
      const existingQueries = new Set(prev.map((k) => k.query.toLowerCase()));
      const uniqueNew = newKws.filter((k) => !existingQueries.has(k.query.toLowerCase()));
      return [...uniqueNew, ...prev];
    });
    setSnackbarMessage(`Imported ${newKws.length} measured queries from Search Console CSV.`);
  };

  const handleAddNegative = (neg: NegativeKeyword) => {
    setNegatives((prev) => [neg, ...prev]);
    setSnackbarMessage(`Added negative keyword: "${neg.term}".`);
  };

  // Handlers for Module 2
  const handleUpdateProperty = (id: string, updated: Partial<PropertySeoItem>) => {
    setProperties((prev) => prev.map((p) => (p.id === id ? { ...p, ...updated } : p)));
  };

  // Handlers for Module 3
  const handleUpdateHub = (id: string, updated: Partial<LocalityHubSeoItem>) => {
    setLocalityHubs((prev) => prev.map((h) => (h.id === id ? { ...h, ...updated } : h)));
  };

  const handleCreateHub = async (newHub: LocalityHubSeoItem) => {
    try {
      await WebsiteCmsService.saveLocalityHub(newHub);
      setLocalityHubs((prev) => [newHub, ...prev]);
      setSnackbarMessage(`Locality page "${newHub.title}" saved to the website database. Verify the public URL before sharing it.`);
    } catch (err) {
      console.warn('handleCreateHub failed:', err);
      setSnackbarMessage('Locality page could not be saved. Check access and try again.');
      throw err;
    }
  };

  // Handlers for Module 4
  const handleUpdateHomepage = (updated: Partial<PortalHomepageSeoItem>) => {
    setHomepageSeo((prev) => ({ ...prev, ...updated }));
  };

  // Handlers for Safe Sync & Queue
  const handleImmediateSync = async (snapshot: SeoSnapshotBackup) => {
      if (snapshot.entityType === 'PROPERTY') {
        const prop = properties.find((p) => p.id === snapshot.entityId);
        await WebsiteCmsService.syncPropertySeo({
          projectKey: prop?.projectKey || 'swarna-avsar',
          projectName: snapshot.entityName,
          seoTitle: snapshot.snapshotData.seoTitle,
          metaDescription: snapshot.snapshotData.metaDescription,
          faqs: snapshot.snapshotData.faqs,
          schemaJson: snapshot.snapshotData.schemaJson,
        });
      } else if (snapshot.entityType === 'LOCALITY') {
        const hub = localityHubs.find((h) => h.id === snapshot.entityId);
        if (!hub) throw new Error('Locality page could not be found.');
        await WebsiteCmsService.saveLocalityHub({
          ...hub,
          seoTitle: snapshot.snapshotData.seoTitle,
          metaDescription: snapshot.snapshotData.metaDescription,
          richGuideHtml: snapshot.snapshotData.richContentHtml,
          faqs: snapshot.snapshotData.faqs,
          schemaJson: snapshot.snapshotData.schemaJson,
          syncStatus: 'synced',
        });
      } else if (snapshot.entityType === 'HOMEPAGE') {
        await WebsiteCmsService.syncHomepageSeo({
          seoTitle: snapshot.snapshotData.seoTitle,
          metaDescription: snapshot.snapshotData.metaDescription,
          brandStoryHtml: snapshot.snapshotData.richContentHtml,
          faqs: snapshot.snapshotData.faqs,
          schemaJson: snapshot.snapshotData.schemaJson,
        });
      }
    setSnackbarMessage(`${snapshot.entityName} saved to the website database. Check the public page to verify.`);
  };

  const handleQueueRelease = (queueItem: ScheduledReleaseItem) => {
    setReleaseQueue((prev) => [queueItem, ...prev]);
    setSnackbarMessage(`⏳ Snapshot saved & staged for release (${queueItem.scheduledFor}).`);
  };

  const handleRollback = (backup: SeoSnapshotBackup) => {
    setSnackbarMessage(`🔄 Successfully rolled back ${backup.entityName} to version ${backup.timestamp}.`);
  };

  const handleApplyQueuedItem = async (queueId: string) => {
    const item = releaseQueue.find((q) => q.id === queueId);
    if (!item) return;

    if (item.entityType === 'PROPERTY') {
      handleUpdateProperty(item.entityId, {
        seoTitle: item.targetMetadata.seoTitle,
        metaDescription: item.targetMetadata.metaDescription,
        highlightsHtml: item.targetMetadata.richContentHtml,
        faqs: item.targetMetadata.faqs,
        schemaJson: item.targetMetadata.schemaJson,
        syncStatus: 'synced',
        lastSyncedAt: new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) + ' IST',
      });
      const prop = properties.find((p) => p.id === item.entityId);
      await WebsiteCmsService.syncPropertySeo({
        projectKey: prop?.projectKey || 'swarna-avsar',
        projectName: item.entityName,
        seoTitle: item.targetMetadata.seoTitle,
        metaDescription: item.targetMetadata.metaDescription,
        faqs: item.targetMetadata.faqs,
        schemaJson: item.targetMetadata.schemaJson,
      });
    } else if (item.entityType === 'LOCALITY') {
      handleUpdateHub(item.entityId, {
        seoTitle: item.targetMetadata.seoTitle,
        metaDescription: item.targetMetadata.metaDescription,
        richGuideHtml: item.targetMetadata.richContentHtml,
        faqs: item.targetMetadata.faqs,
        schemaJson: item.targetMetadata.schemaJson,
        syncStatus: 'synced',
        lastSyncedAt: new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) + ' IST',
      });
      const hub = localityHubs.find((h) => h.id === item.entityId);
      await WebsiteCmsService.syncLocalitySeo({
        slug: hub?.slug || 'locality',
        localityName: item.entityName,
        seoTitle: item.targetMetadata.seoTitle,
        metaDescription: item.targetMetadata.metaDescription,
        richGuideHtml: item.targetMetadata.richContentHtml,
        faqs: item.targetMetadata.faqs,
        schemaJson: item.targetMetadata.schemaJson,
      });
    } else if (item.entityType === 'HOMEPAGE') {
      handleUpdateHomepage({
        seoTitle: item.targetMetadata.seoTitle,
        metaDescription: item.targetMetadata.metaDescription,
        brandStoryHtml: item.targetMetadata.richContentHtml,
        faqs: item.targetMetadata.faqs,
        schemaJson: item.targetMetadata.schemaJson,
        syncStatus: 'synced',
        lastSyncedAt: new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) + ' IST',
      });
      await WebsiteCmsService.syncHomepageSeo({
        seoTitle: item.targetMetadata.seoTitle,
        metaDescription: item.targetMetadata.metaDescription,
        brandStoryHtml: item.targetMetadata.richContentHtml,
        faqs: item.targetMetadata.faqs,
        schemaJson: item.targetMetadata.schemaJson,
      });
    }

    setReleaseQueue((prev) => prev.map((q) => (q.id === queueId ? { ...q, status: 'applied' } : q)));
    setSnackbarMessage(`Released & Applied queued update for ${item.entityName}.`);
  };

  const handleCancelQueuedItem = (queueId: string) => {
    setReleaseQueue((prev) => prev.filter((q) => q.id !== queueId));
    setSnackbarMessage('Cancelled staged release.');
  };

  // Google Ads Handlers
  const handleAddAdGroup = (grp: GoogleAdGroup) => {
    setAdGroups((prev) => [grp, ...prev]);
  };

  const handleUpdateAdGroup = (id: string, updated: Partial<GoogleAdGroup>) => {
    setAdGroups((prev) => prev.map((g) => (g.id === id ? { ...g, ...updated } : g)));
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3.5 }, maxWidth: 1600, margin: '0 auto' }}>
      {loadingInitial && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      <Alert severity="info" sx={{ mb: 3 }}>
        Use this workspace for measured Search Console imports and locality content. Imported query data and Google Ads plans are saved in this browser only. Edit project and homepage metadata in Website CMS. Publishing to the website database does not guarantee Google indexing.
        <Button size="small" href="/website-cms" sx={{ ml: 1 }}>Open Website CMS</Button>
      </Alert>

      {/* Main Module Tabs */}
      <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid #e8e8e8', bgcolor: '#fff', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 2,
            borderBottom: '1px solid #e8e8e8',
            '& .MuiTab-root': { py: 2, fontWeight: 700, fontSize: 13.5 },
          }}
        >
          <Tab value={0} icon={<SearchIcon />} iconPosition="start" label="Search Console queries" />
          <Tab value={2} icon={<LocationOnIcon />} iconPosition="start" label="Locality content" />
          <Tab value={4} icon={<AdsClickIcon />} iconPosition="start" label="Google Ads CSV planning (local)" />
        </Tabs>

        {/* Tab Contents */}
        <Box sx={{ p: 3 }}>
          {activeTab === 0 && (
            <KeywordIntelligenceTab
              keywords={keywords}
              negativeKeywords={negatives}
              onAddKeyword={handleAddKeyword}
              onExcludeKeyword={handleExcludeKeyword}
              onRemoveNegative={handleRemoveNegative}
              onAddNegative={handleAddNegative}
              onMergeKeywords={handleMergeKeywords}
            />
          )}

          {activeTab === 1 && (
            <PropertyListingsSeoTab
              properties={properties}
              backups={backups}
              onUpdateProperty={handleUpdateProperty}
              onImmediateSync={handleImmediateSync}
              onQueueRelease={handleQueueRelease}
              onRollback={handleRollback}
            />
          )}

          {activeTab === 2 && (
            <LocalityHubsSeoTab
              localityHubs={localityHubs}
              backups={backups}
              onUpdateHub={handleUpdateHub}
              onCreateHub={handleCreateHub}
              onImmediateSync={handleImmediateSync}
              onQueueRelease={handleQueueRelease}
              onRollback={handleRollback}
            />
          )}

          {activeTab === 3 && (
            <PortalHomepageSeoTab
              homepageSeo={homepageSeo}
              backups={backups}
              onUpdateHomepage={handleUpdateHomepage}
              onImmediateSync={handleImmediateSync}
              onQueueRelease={handleQueueRelease}
              onRollback={handleRollback}
            />
          )}

          {activeTab === 4 && (
            <GoogleAdsBridgeTab
              adGroups={adGroups}
              onAddAdGroup={handleAddAdGroup}
              onUpdateAdGroup={handleUpdateAdGroup}
            />
          )}

          {activeTab === 5 && (
            <Box>
              {/* Scheduled Release Queue */}
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                ⏳ Scheduled Off-Peak Release Queue
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Metadata staged for off-peak release window to ensure indexing stability.
              </Typography>

              {releaseQueue.length === 0 ? (
                <Alert severity="info" sx={{ mb: 4 }}>
                  No items currently staged in the release queue.
                </Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 4, borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#fafafa' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Entity & Type</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Target Release Window</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Queued At</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {releaseQueue.map((q) => (
                        <TableRow key={q.id}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {q.entityName}
                            </Typography>
                            <Chip label={q.entityType} size="small" sx={{ fontSize: 10, height: 18 }} />
                          </TableCell>
                          <TableCell>
                            <Chip icon={<ScheduleSendIcon />} label={q.scheduledFor} size="small" color="primary" sx={{ bgcolor: '#9d6f32' }} />
                          </TableCell>
                          <TableCell>{q.queuedAt}</TableCell>
                          <TableCell>
                            <Chip
                              label={q.status.toUpperCase()}
                              size="small"
                              color={q.status === 'applied' ? 'success' : 'warning'}
                              sx={{ fontWeight: 700 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            {q.status === 'pending' && (
                              <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="success"
                                  onClick={() => handleApplyQueuedItem(q.id)}
                                  sx={{ fontSize: 11, fontWeight: 700 }}
                                >
                                  Release Now
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  onClick={() => handleCancelQueuedItem(q.id)}
                                  sx={{ fontSize: 11 }}
                                >
                                  Cancel
                                </Button>
                              </Stack>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Snapshot Pre-Sync Backups & Rollbacks */}
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                🛡️ Snapshot Pre-Sync Backups & 1-Click Rollbacks (seo_backups)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Every live sync records an immutable snapshot. Restore any entity with 1-click if rankings fluctuate.
              </Typography>

              {backups.length === 0 ? (
                <Alert severity="info">No snapshots recorded yet. Click "Safe Sync" on any entity to create your first backup.</Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#fafafa' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Snapshot Timestamp</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Entity Name</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Entity Type</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Snapshot SEO Title</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Trigger Reason</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Rollback</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {backups.map((b) => (
                        <TableRow key={b.id} hover>
                          <TableCell sx={{ fontWeight: 600 }}>{b.timestamp}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{b.entityName}</TableCell>
                          <TableCell>
                            <Chip label={b.entityType} size="small" variant="outlined" sx={{ fontSize: 10, height: 20 }} />
                          </TableCell>
                          <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {b.snapshotData.seoTitle}
                          </TableCell>
                          <TableCell>
                            <Chip label={b.changeReason} size="small" color="primary" variant="outlined" sx={{ fontSize: 10, height: 20 }} />
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<RestoreIcon />}
                              onClick={() => handleRollback(b)}
                              sx={{ fontWeight: 700, fontSize: 11 }}
                            >
                              1-Click Rollback
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </Box>
      </Paper>

      {/* Snackbar feedback */}
      <Snackbar
        open={Boolean(snackbarMessage)}
        autoHideDuration={4000}
        onClose={() => setSnackbarMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbarMessage(null)} severity="success" sx={{ width: '100%', fontWeight: 600 }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
